import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { QuillEditorComponent } from 'ngx-quill';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import { SubSink } from 'subsink';

import { BaseZoneFragment, CreatePageGQL, EditPageGQL, FullPageWithContentsFragment, PagesGQL, RemovePageGQL, ZoneDir, } from '../../generated/graphql.generated';
import { BrandingService } from '../services/branding.service';
import { SharedModule } from '../shared/shared.module';

@Component({
  selector: 'app-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    QuillEditorComponent,
    ProgressSpinnerModule,
    ButtonModule,
    InputTextModule,
    ConfirmDialogModule,
    SharedModule,
    TagModule,
    TooltipModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './page.component.html',
  styleUrl: './page.component.scss'
})
export class PageComponent implements OnInit, OnDestroy {

  subs = new SubSink();

  loading = true;
  path: string;

  currentZonePage: FullPageWithContentsFragment;
  page: FullPageWithContentsFragment;
  pagesWithDirectory: FullPageWithContentsFragment[] = [];

  currentZone: BaseZoneFragment;
  altZoneSelected: string;

  disableEdit = false;
  disableRemove = false;

  saveText = 'Save';

  editMode = false;
  editTitle = '';
  editContents: any;

  viewModules = {
    toolbar: false,
  };

  editModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
      // ['blockquote', 'code-block'],

      // [{ 'header': 1 }, { 'header': 2 }],               // custom button values
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': "check" }],
      // [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
      // [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
      // [{ 'direction': 'rtl' }],                         // text direction

      // [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
      // [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

      [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
      // [{ 'font': [] }],
      // [{ 'align': [] }],

      // ['clean'],                                         // remove formatting button

      [ 'link' ]
      // [ 'link', 'image', 'video' ]                         // link and image, video
    ],

    variableToolbar: true,
    templateToolbar: true,
  };

  watchQuery: ReturnType<typeof this.ListPageGQL.watch>;

  constructor(
    public ListPageGQL: PagesGQL,
    public CreatePageGQL: CreatePageGQL,
    public EditPageGQL: EditPageGQL,
    public RemovePageGQL: RemovePageGQL,
    private confirmationService: ConfirmationService,
    private brandingSvc: BrandingService,
  ) {

  }

  ngOnInit(): void {

    this.subs.sink = this.brandingSvc.currentZone().subscribe((zone) => {
      this.currentZone = zone;
      this.checkFlags();
    });

    this.watchQuery = this.ListPageGQL.watch(this.getVariablesFromCurrentPath(), {
      fetchPolicy: 'cache-and-network',
      useInitialLoading: true,
    });

    // TODO: CHECK ERROR
    this.subs.sink = this.watchQuery.valueChanges.subscribe(({ data, loading }) => {
      
      this.loading = loading;
      this.pagesWithDirectory = data?.pages?.pages || [];
  
      this.selectPage();
    });
  }

  selectPage() {
    // Select an alt selected zone or the first zone, if we
    // cant find the page based on the zone
    const zone = this.altZoneSelected || this.currentZone?.id;
    this.page = this.pagesWithDirectory.find((p) => p.zone?.id === zone);

    this.currentZonePage = this.pagesWithDirectory.find((p) => p.zone?.id === this.currentZone?.id);

    if (!this.page && this.pagesWithDirectory?.length && this.currentZone) {
      // select LAST page above the current zone
      this.page = this.pagesWithDirectory
        // reverse the array without mutating it
        // .slice().reverse()
        .find((p) => p.zone.zoneDepth <= this.currentZone.zoneDepth);

    }

    this.checkFlags();
  }

  setAlternative(page: FullPageWithContentsFragment) {
    if (page === this.page) { return; }

    this.altZoneSelected = page.zone.id;
    this.selectPage();
  }

  checkFlags() {
    this.disableEdit = !(this.currentZonePage?.id === this.page?.id || !this.currentZonePage);
    this.disableRemove = !(this.currentZonePage?.id === this.page?.id && this.page);
    this.saveText = `Save`;

    if (!this.page) {
      this.saveText = `Save new page`;
    } else if (this.currentZonePage?.id !== this.page?.id) {
      this.saveText = `Save in ${ this.currentZone.name }`
    }
  }

  getVariablesFromCurrentPath() {
    const url = new URL(location.href);
    this.path = url.pathname;

    return {
      filter: {
        path: this.path,
        zoneDir: ZoneDir.Any,
      },
      limit: 100,
      skip: 0,
    };
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  enterEditMode() {
    this.editTitle = this.page ? this.page.title : '';
    this.editContents = this.page ? this.page.contents : null;
    this.editMode = true;
    this.checkFlags();
  }

  cancelEdit() {
    this.editMode = false;
  }

  savePage() {
    if (this.page && this.page?.id === this.currentZonePage?.id) {
      // Edit existing page
      this.subs.sink = this.EditPageGQL.mutate({
        pageId: this.page.id,
        pageEdit: {
          title: this.editTitle,
          contents: this.editContents,
        }
      }).subscribe(() => {
        this.altZoneSelected = undefined;
        this.watchQuery.refetch();
        this.editMode = false;
      });
    } else if (!this.currentZonePage) {
      // Create new page
      this.subs.sink = this.CreatePageGQL.mutate({
        page: {
          path: this.path,
          title: this.editTitle,
          contents: this.editContents,
          // zones: this.isDuplicate ? [this.currentZone.id] : [],
        }
      }).subscribe(() => {
        this.altZoneSelected = undefined;
        this.watchQuery.refetch();
        this.editMode = false;
      });
    }
  }

  confirmRemove() {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this page?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.removePage();
      }
    });
  }

  removePage() {
    if (!this.page) {
      return;
    }

    this.subs.sink = this.RemovePageGQL.mutate({ pageId: this.page.id }).subscribe(() => {
      // TODO: Should probably navigate away after delete
      this.watchQuery.refetch();
    });
  }

  createPage() {
    this.enterEditMode();
  }
}
